import React, { useEffect, useRef, useState, useCallback } from 'react';

const conversation = [
  { type: 'user', text: 'Hello' },
  { type: 'ai', text: 'Hi there! How may I help you today?' },
  { type: 'user', text: 'Generate me a Python script example that analyzes audio frequencies' },
  {
    type: 'ai',
    text: "Sure, I can create a Python script that analyzes audio frequencies. Here's an example using librosa:",
    code: [
      { text: '# Audio Frequency Analysis Example', type: 'comment' },
      { text: 'import numpy as np', type: 'keyword' },
      { text: 'import librosa', type: 'keyword' },
      { text: 'import matplotlib.pyplot as plt', type: 'keyword' },
      { text: '', type: '' },
      { text: '# Function to load and analyze audio file', type: 'comment' },
      { text: 'def analyze_audio_frequencies(file_path):', type: 'function' },
      { text: '    # Load audio file', type: 'comment' },
      { text: '    y, sr = librosa.load(file_path, sr=None)', type: 'normal' },
      { text: '    ', type: 'normal' },
      { text: '    # Calculate the Short-Time Fourier Transform (STFT)', type: 'comment' },
      { text: '    D = np.abs(librosa.stft(y))', type: 'normal' },
      { text: '    ', type: 'normal' },
      { text: '    # Convert amplitude to decibels', type: 'comment' },
      { text: '    DB = librosa.amplitude_to_db(D, ref=np.max)', type: 'normal' },
      { text: '    ', type: 'normal' },
      { text: '    # Get frequency bins', type: 'comment' },
      { text: '    freq_bins = librosa.fft_frequencies(sr=sr)', type: 'normal' },
      { text: '    ', type: 'normal' },
      { text: '    # Get time frames', type: 'comment' },
      { text: '    time_frames = librosa.times_like(D, sr=sr)', type: 'normal' },
      { text: '    ', type: 'normal' },
      { text: '    return {', type: 'normal' },
      { text: '        "spectrogram": DB,', type: 'normal' },
      { text: '        "frequencies": freq_bins,', type: 'normal' },
      { text: '        "times": time_frames,', type: 'normal' },
      { text: '        "sample_rate": sr', type: 'normal' },
      { text: '    }', type: 'normal' },
      { text: '', type: '' },
      { text: '# Function to visualize the spectrogram', type: 'comment' },
      { text: 'def plot_spectrogram(analysis_result):', type: 'function' },
      { text: '    plt.figure(figsize=(12, 6))', type: 'normal' },
      { text: '    librosa.display.specshow(', type: 'function' },
      { text: '        analysis_result["spectrogram"],', type: 'normal' },
      { text: '        sr=analysis_result["sample_rate"],', type: 'normal' },
      { text: '        x_axis="time",', type: 'string' },
      { text: '        y_axis="log",', type: 'string' },
      { text: '        cmap="viridis"', type: 'string' },
      { text: '    )', type: 'normal' },
      { text: '    plt.colorbar(format="%+2.0f dB")', type: 'normal' },
      { text: '    plt.title("Audio Frequency Spectrogram")', type: 'normal' },
      { text: '    plt.tight_layout()', type: 'normal' },
      { text: '    plt.show()', type: 'normal' },
      { text: '', type: '' },
      { text: '# Example usage', type: 'comment' },
      { text: 'if __name__ == "__main__":', type: 'keyword' },
      { text: '    # Replace with your audio file path', type: 'comment' },
      { text: '    audio_file = "example_audio.wav"', type: 'string' },
      { text: '    ', type: 'normal' },
      { text: '    # Analyze frequencies', type: 'comment' },
      { text: '    result = analyze_audio_frequencies(audio_file)', type: 'normal' },
      { text: '    ', type: 'normal' },
      { text: '    # Plot the spectrogram', type: 'comment' },
      { text: '    plot_spectrogram(result)', type: 'normal' },
      { text: '    ', type: 'normal' },
      { text: '    # Print frequency range', type: 'comment' },
      { text: '    print(f"Frequency range: {result[\'frequencies\'][0]:.1f} Hz to {result[\'frequencies\'][-1]:.1f} Hz")', type: 'normal' }
    ],
  }
];

function AudiosumChat() {
  const outerRef = useRef(null); // for observer
  const scrollRef = useRef(null); // for scrolling
  const bgElementsRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentCodeLineIndex, setCurrentCodeLineIndex] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const animationTimeout = useRef(null);
  const [scrollThumb, setScrollThumb] = useState({ top: 0, height: 40 });
  const [animationStarted, setAnimationStarted] = useState(false);
  const observerRef = useRef(null);

  // Custom scrollbar logic
  const updateScrollThumb = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const thumbHeight = 40; // fixed thumb height
    const maxThumbTop = clientHeight - thumbHeight;
    const maxScrollTop = scrollHeight - clientHeight;
    let top = 0;
    if (maxScrollTop > 0) {
      top = (scrollTop / maxScrollTop) * maxThumbTop;
    }
    setScrollThumb({ top, height: thumbHeight });
  }, []);

  // Only scroll to bottom after a full message or code block is revealed
  const scrollToBottom = useCallback((customDuration) => {
    const container = scrollRef.current;
    if (!container) return;
    const target = container.scrollHeight - container.clientHeight;
    if (Math.abs(container.scrollTop - target) > 2) {
      if (customDuration) {
        // Custom smooth scroll for last chat
        const start = container.scrollTop;
        const change = target - start;
        const duration = customDuration;
        let startTime = null;
        function animateScroll(timestamp) {
          if (!startTime) startTime = timestamp;
          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / duration, 1);
          container.scrollTop = start + change * easeInOutQuad(progress);
          if (progress < 1) {
            requestAnimationFrame(animateScroll);
          }
        }
        function easeInOutQuad(t) {
          return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        }
        requestAnimationFrame(animateScroll);
      } else {
        container.scrollTo({ top: target, behavior: 'smooth' });
      }
    }
    updateScrollThumb();
  }, [updateScrollThumb]);

  // Clear timeout helper
  const clearAnimationTimeout = () => {
    if (animationTimeout.current) {
      clearTimeout(animationTimeout.current);
      animationTimeout.current = null;
    }
  };

  // Add next message or code line
  const showNextMessage = (msgIndex = currentMessageIndex, codeLineIndex = currentCodeLineIndex) => {
    if (msgIndex >= conversation.length) {
      // Restart after delay
      animationTimeout.current = setTimeout(() => {
        setMessages([]);
        setCurrentMessageIndex(0);
        setCurrentCodeLineIndex(0);
        setShowTyping(false);
        setTimeout(() => {
          setAnimationStarted(false); // allow observer to restart
        }, 100);
      }, 3000);
      return;
    }
    const message = conversation[msgIndex];
    // If this is the last message and has code, paste all code instantly
    if (msgIndex === conversation.length - 1 && message.code) {
      setMessages((prev) => [...prev, { ...message, codeVisibleLines: message.code.length }]);
      setShowTyping(false);
      setTimeout(() => scrollToBottom(1200), 80); // even slower scroll for last chat
      setCurrentMessageIndex(msgIndex + 1);
      animationTimeout.current = setTimeout(() => {
        showNextMessage(msgIndex + 1, 0);
      }, 2000); // 2s delay between loops
      return;
    }
    if (message.code) {
      if (codeLineIndex === 0) {
        setMessages((prev) => [...prev, { ...message, codeVisibleLines: 0 }]);
        setShowTyping(false);
        animationTimeout.current = setTimeout(() => {
          showNextMessage(msgIndex, 1);
        }, 1000);
      } else if (codeLineIndex <= message.code.length) {
        setMessages((prev) =>
          prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, codeVisibleLines: codeLineIndex } : m
          )
        );
        if (codeLineIndex === message.code.length) {
          setTimeout(scrollToBottom, 80);
        }
        animationTimeout.current = setTimeout(() => {
          showNextMessage(msgIndex, codeLineIndex + 1);
        }, 100);
      } else {
        setCurrentMessageIndex(msgIndex + 1);
        setCurrentCodeLineIndex(0);
        if (msgIndex + 1 < conversation.length && conversation[msgIndex + 1].type === 'ai') {
          setShowTyping(true);
          animationTimeout.current = setTimeout(() => {
            setShowTyping(false);
            showNextMessage(msgIndex + 1, 0);
          }, 1500);
        } else {
          animationTimeout.current = setTimeout(() => {
            showNextMessage(msgIndex + 1, 0);
          }, 1000);
        }
      }
    } else {
      setMessages((prev) => [...prev, message]);
      setTimeout(scrollToBottom, 80);
      setCurrentMessageIndex(msgIndex + 1);
      if (msgIndex + 1 < conversation.length && conversation[msgIndex + 1].type === 'ai') {
        setShowTyping(true);
        animationTimeout.current = setTimeout(() => {
          setShowTyping(false);
          showNextMessage(msgIndex + 1, 0);
        }, 1500);
      } else {
        animationTimeout.current = setTimeout(() => {
          showNextMessage(msgIndex + 1, 0);
        }, 1000);
      }
    }
  };

  // Matrix background effect
  useEffect(() => {
    // Exact logic from reference
    function createMatrixCode() {
      const matrixChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,./<>?";
      const purpleColors = ['#a855f7', '#9333ea', '#c084fc', '#8b5cf6'];
      function createMatrixColumn(xPercent, color) {
        const column = document.createElement('div');
        column.className = 'matrix-column';
        column.style.left = `${xPercent}%`;
        const duration = Math.random() * 15 + 10;
        column.style.animation = `matrixRain ${duration}s linear infinite`;
        column.style.animationDelay = `${Math.random() * -20}s`;
        column.style.opacity = `${Math.random() * 0.4 + 0.6}`;
        column.style.color = color;
        column.style.textShadow = `0 0 5px ${color}`;
        const numChars = Math.floor(Math.random() * 15) + 10;
        for (let j = 0; j < numChars; j++) {
          const char = document.createElement('div');
          char.className = 'matrix-char';
          char.textContent = matrixChars.charAt(Math.floor(Math.random() * matrixChars.length));
          char.style.animationDelay = `${Math.random() * 2}s`;
          column.appendChild(char);
          setInterval(() => {
            if (Math.random() > 0.8) {
              char.textContent = matrixChars.charAt(Math.floor(Math.random() * matrixChars.length));
            }
          }, 300);
        }
        if (bgElementsRef.current) {
          bgElementsRef.current.appendChild(column);
        }
      }
      // Create streams on the left side (0-30% of screen width)
      for (let i = 0; i < 8; i++) {
        createMatrixColumn(
          Math.random() * 30,
          purpleColors[Math.floor(Math.random() * purpleColors.length)]
        );
      }
      // Create streams on the right side (70-100% of screen width)
      for (let i = 0; i < 8; i++) {
        createMatrixColumn(
          70 + Math.random() * 30,
          purpleColors[Math.floor(Math.random() * purpleColors.length)]
        );
      }
    }
    createMatrixCode();
  }, []);

  // IntersectionObserver: start animation on scroll into view, allow looping
  useEffect(() => {
    if (animationStarted) return;
    const elem = outerRef.current;
    if (!elem) return;
    observerRef.current = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !animationStarted) {
          setAnimationStarted(true);
          setMessages([]);
          setCurrentMessageIndex(0);
          setCurrentCodeLineIndex(0);
          setShowTyping(false);
          setTimeout(() => showNextMessage(0, 0), 200);
        }
      });
    }, { threshold: 0.5 });
    observerRef.current.observe(elem);
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      clearAnimationTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationStarted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAnimationTimeout();
    };
  }, []);

  // Update scroll thumb when messages change
  useEffect(() => {
    if (animationStarted) {
      setTimeout(updateScrollThumb, 300);
    }
  }, [messages, showTyping, updateScrollThumb, animationStarted]);

  // Also update thumb on manual scroll
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateScrollThumb);
    return () => container.removeEventListener('scroll', updateScrollThumb);
  }, [updateScrollThumb]);

  // JSX rendering helpers
  const renderProfilePic = (type) => {
    if (type === 'user') {
      return (
        <div className="profile-pic user-pic" aria-label="User">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="profile-pic ai-pic" aria-label="AI">
          AI
        </div>
      );
    }
  };

  return (
    <div className="chat-interface-container bg-matrix" ref={outerRef}>
      <div ref={bgElementsRef} id="bg-elements"></div>
      <div className="carousel-container" aria-live="polite">
        <div className="chat-container">
          <div className="chat-header">
            <div className="chat-logo">AI</div>
            <div className="chat-title">Audiosum</div>
          </div>
          <div
            className="chat-messages"
            id="chatMessages"
            tabIndex={-1}
            style={{ position: 'relative', overflow: 'hidden', paddingRight: 16 }}
          >
            <div
              ref={scrollRef}
              style={{ height: '100%', overflowY: 'auto', paddingRight: 0, position: 'relative', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`message-container ${msg.type === 'user' ? 'user-container' : 'ai-container'} visible`}
                  aria-label={`${msg.type === 'user' ? 'User message' : 'AI message'}`}
                  style={{ marginBottom: i === messages.length - 1 ? 24 : 0, marginTop: i === messages.length - 1 ? 16 : 0 }}
                >
                  {renderProfilePic(msg.type)}
                  <div className={`message ${msg.type}-message`}>
                    <div>{msg.text}</div>
                    {msg.code && (
                      <div className="code-block">
                        {msg.code.slice(0, msg.codeVisibleLines || 0).map((line, idx) => {
                          let innerHTML = line.text;
                          if (line.type === 'keyword') {
                            innerHTML = <span className="keyword">{line.text}</span>;
                          } else if (line.type === 'function') {
                            innerHTML = <span className="function">{line.text}</span>;
                          } else if (line.type === 'string') {
                            innerHTML = <span className="string">{line.text}</span>;
                          } else if (line.type === 'number') {
                            innerHTML = <span className="number">{line.text}</span>;
                          } else if (line.type === 'comment') {
                            innerHTML = <span className="comment">{line.text}</span>;
                          }
                          return (
                            <div key={idx} className={`code-line visible`}>
                              <span className="line-number">{idx + 1}</span>
                              <span className="line-content">{innerHTML}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {showTyping && (
                <div className="message-container ai-container visible" id="typing-container" aria-live="off">
                  {renderProfilePic('ai')}
                  <div className="message ai-message typing-indicator" aria-label="Typing indicator">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              )}
            </div>
            {/* Custom Scrollbar Thumb */}
            <div
              className="custom-scrollbar-thumb"
              style={{
                position: 'absolute',
                right: 2,
                top: scrollThumb.top,
                width: 8,
                height: scrollThumb.height,
                background: 'rgba(153,102,255,0.7)',
                borderRadius: 4,
                zIndex: 20,
                transition: 'top 0.2s cubic-bezier(.4,1.2,.4,1)',
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>
      </div>
      <style>{`
        .chat-interface-container {
          width: 100%;
          height: 500px;
          position: relative;
          overflow: hidden;
          background-color: #1A0933;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          transition: background 1s ease;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px 0;
        }
        .carousel-container {
         background-color: #1A0933;
          width: 100%;
          max-width: 900px;
          // height: 400px;
          position: relative;
          overflow: visible;
          border-radius: 12px;
          box-shadow: none;
          // background: transparent !important;
          z-index: 10;
        }
        .chat-container {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: 20px;
          box-sizing: border-box;
          overflow: hidden;
          background: transparent !important;
        }
        .chat-header {
          display: flex;
          align-items: center;
          margin-bottom: 20px;
        }
        .chat-logo {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #9966FF 0%, #6633CC 100%);
          display: flex;
          justify-content: center;
          align-items: center;
          margin-right: 15px;
          box-shadow: 0 0 10px rgba(153, 102, 255, 0.5);
          color: white;
          font-weight: bold;
          font-size: 16px;
        }
        .chat-title {
          color: white;
          font-size: 24px;
          font-weight: 600;
        }
        .chat-messages {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding-right: 0;
          scroll-behavior: smooth;
          background: transparent !important;
        }
        .chat-messages > div {
          height: 100%;
          overflow-y: auto;
          padding-right: 0;
          position: relative;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE 10+ */
        }
        .chat-messages > div::-webkit-scrollbar {
          width: 0 !important;
          background: transparent !important;
        }
        .message-container {
          display: flex;
          gap: 12px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s, transform 0.5s;
          width: 100%;
        }
        .message-container.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .user-container {
          justify-content: flex-end;
        }
        .ai-container {
          justify-content: flex-start;
        }
        .profile-pic {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          color: white;
          font-weight: bold;
          font-size: 14px;
        }
        .user-pic {
          background-color: #FF3366;
          order: 2;
        }
        .ai-pic {
          background: linear-gradient(135deg, #9966FF 0%, #6633CC 100%);
          order: 0;
        }
        .message {
          max-width: 70%;
          padding: 12px 16px;
          border-radius: 18px;
          font-size: 15px;
          line-height: 1.4;
        }
        .user-message {
          background-color: #6633CC;
          color: white;
          border-bottom-right-radius: 4px;
          order: 1;
          margin-right: 4px;
        }
        .ai-message {
          background-color: rgba(255, 255, 255, 0.1);
          color: white;
          border-bottom-left-radius: 4px;
          order: 1;
          margin-left: 4px;
        }
        .code-block {
          background-color: rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          padding: 12px;
          margin-top: 8px;
          font-family: 'Consolas', 'Monaco', monospace;
          font-size: 14px;
          overflow: hidden;
        }
        .code-line {
          height: 20px;
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          opacity: 0;
          transform: translateX(-10px);
          transition: opacity 0.3s, transform 0.3s;
        }
        .code-line.visible {
          opacity: 1;
          transform: translateX(0);
        }
        .line-number {
          color: rgba(255, 255, 255, 0.4);
          min-width: 20px;
          margin-right: 10px;
          text-align: right;
          user-select: none;
        }
        .line-content {
          white-space: pre;
        }
        .typing-indicator {
          display: flex;
          gap: 4px;
          padding: 12px 16px;
        }
        .typing-dot {
          width: 8px;
          height: 8px;
          background-color: #9966FF;
          border-radius: 50%;
          opacity: 0.7;
        }
        .typing-dot:nth-child(1) {
          animation: bounce 1.4s infinite 0s;
        }
        .typing-dot:nth-child(2) {
          animation: bounce 1.4s infinite 0.2s;
        }
        .typing-dot:nth-child(3) {
          animation: bounce 1.4s infinite 0.4s;
        }
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
        .keyword { color: #FF3366; }
        .function { color: #FFCC33; }
        .string { color: #33CC66; }
        .number { color: #66CCFF; }
        .comment { color: #999999; }
        /* Matrix background styles */
        .bg-matrix {
          background-color: #0c0020;
          position: relative;
          overflow: hidden;
        }
        .matrix-column {
          position: absolute;
          top: -20px;
          font-family: monospace;
          font-size: 14px;
          color: #a855f7;
          text-shadow: 0 0 5px #9333ea;
          z-index: 0;
          white-space: nowrap;
          opacity: 0.8;
        }
        .matrix-char {
          display: block;
          text-align: center;
          animation: fadeMatrix 2s infinite;
        }
        @keyframes fadeMatrix {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        @keyframes matrixRain {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400px); }
        }
      `}</style>
    </div>
  );
}

export default AudiosumChat; 